// src/pages/LiveMapPage.jsx
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { EmptyState } from '../components/UI';
import { usePolling } from '../hooks/usePolling';
import api from '../services/api';

async function fetchLocations() {
  const res = await api.get('/location/riders', { timeout: 8000 });
  return res.data;
}

const RIDER_COLORS = ['#F4A61C', '#3B82F6', '#22C55E', '#EC4899', '#A855F7', '#F97316', '#14B8A6'];
function riderColor(idx) { return RIDER_COLORS[idx % RIDER_COLORS.length]; }

function staleness(updatedAt) {
  const secs = Math.floor((Date.now() - new Date(updatedAt)) / 1000);
  if (secs < 60) return { label: `${secs}s ago`, fresh: true };
  if (secs < 300) return { label: `${Math.floor(secs / 60)}m ago`, fresh: true };
  return { label: `${Math.floor(secs / 60)}m ago - stale`, fresh: false };
}

function formatTime(date) {
  if (!date) return '';
  return new Date(date).toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// ── Smooth marker animation (the Bolt-style glide) ───────────────────────────
// Interpolates a marker from its current lat/lng to a target lat/lng over
// `durationMs` milliseconds using requestAnimationFrame.
function animateMarker(marker, targetLat, targetLng, durationMs = 800) {
  const start = marker.getLatLng();
  const startLat = start.lat;
  const startLng = start.lng;
  const deltaLat = targetLat - startLat;
  const deltaLng = targetLng - startLng;

  // Skip animation for very small movements (< ~1 metre) to avoid jitter
  if (Math.abs(deltaLat) < 0.000009 && Math.abs(deltaLng) < 0.000009) {
    marker.setLatLng([targetLat, targetLng]);
    return;
  }

  const startTime = performance.now();

  function easeInOut(t) {
    // Cubic ease-in-out — smooth acceleration and deceleration
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function step(now) {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / durationMs, 1);
    const eased = easeInOut(progress);
    marker.setLatLng([
      startLat + deltaLat * eased,
      startLng + deltaLng * eased,
    ]);
    if (progress < 1) requestAnimationFrame(step);
  }

  requestAnimationFrame(step);
}

function LeafletMap({ riders, selectedId, onSelect }) {
  const mapRef = useRef(null);
  const leafletRef = useRef(null);
  const markersRef = useRef({});
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
      document.head.appendChild(link);
    }
    const boot = () => {
      const L = window.L;
      leafletRef.current = L;
      if (mapRef.current._leaflet_id) mapRef.current._leaflet_id = null;
      const map = L.map(mapRef.current, { center: [7.2571, 5.2058], zoom: 13 });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: 'OpenStreetMap contributors', maxZoom: 19,
      }).addTo(map);
      leafletRef.current._map = map;
      initialized.current = true;
    };
    if (window.L) { boot(); }
    else {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
      script.onload = boot;
      document.head.appendChild(script);
    }
    return () => {
      if (leafletRef.current && leafletRef.current._map) {
        leafletRef.current._map.remove();
        leafletRef.current._map = null;
        initialized.current = false;
        markersRef.current = {};
      }
    };
  }, []);

  useEffect(() => {
    const L = leafletRef.current;
    if (!L || !L._map) return;
    const map = L._map;

    // Remove markers for riders who went offline
    const currentIds = new Set(riders.map(r => r.rider_id));
    Object.entries(markersRef.current).forEach(([id, marker]) => {
      if (!currentIds.has(parseInt(id))) {
        map.removeLayer(marker);
        delete markersRef.current[id];
      }
    });

    riders.forEach((rider, idx) => {
      const color = riderColor(idx);
      const isSelected = rider.rider_id === selectedId;

      const svgIcon = `<svg width="32" height="42" viewBox="0 0 32 42" xmlns="http://www.w3.org/2000/svg">
        <circle cx="16" cy="16" r="15" fill="${color}" stroke="${isSelected ? '#fff' : 'rgba(0,0,0,0.3)'}" stroke-width="${isSelected ? 3 : 1.5}"/>
        <text x="16" y="21" text-anchor="middle" font-size="14" font-weight="800" fill="${isSelected ? '#0A1628' : '#fff'}">&#x1F6B4;</text>
        <polygon points="10,29 22,29 16,42" fill="${color}"/>
      </svg>`;

      const icon = L.divIcon({
        html: svgIcon, className: '',
        iconSize: [32, 42], iconAnchor: [16, 42], popupAnchor: [0, -42],
      });

      const stale = staleness(rider.updated_at);
      const popup = `<div style="font-family:sans-serif;min-width:180px">
        <div style="font-weight:800;font-size:14px;margin-bottom:4px">${rider.rider_name}</div>
        <div style="font-size:12px;color:#64748b;margin-bottom:6px">${rider.rider_phone || ''}</div>
        ${rider.order_id
          ? `<div style="background:#f1f5f9;border-radius:6px;padding:8px;margin-bottom:4px">
               <div style="font-size:10px;font-weight:700;color:#94a3b8;text-transform:uppercase">Active Order</div>
               <div style="font-size:13px;font-weight:600">#${rider.order_id} - ${rider.order_package_id || ''}</div>
               <div style="font-size:11px;color:#64748b">${rider.order_status || ''} → ${rider.receiver_name || ''}</div>
             </div>`
          : '<div style="font-size:12px;color:#94a3b8">No active order</div>'}
        <div style="font-size:10px;color:#94a3b8;margin-top:4px">Last update: ${stale.label}</div>
        ${rider.speed ? `<div style="font-size:10px;color:#94a3b8">Speed: ${parseFloat(rider.speed).toFixed(1)} km/h</div>` : ''}
      </div>`;

      if (markersRef.current[rider.rider_id]) {
        const m = markersRef.current[rider.rider_id];

        // ✅ SMOOTH ANIMATION — glide to new position instead of jumping
        animateMarker(m, rider.latitude, rider.longitude, 800);

        // Update icon (reflects selected state) and popup content
        m.setIcon(icon);
        m.setPopupContent(popup);
      } else {
        // First time seeing this rider — place immediately, no animation needed
        const m = L.marker([rider.latitude, rider.longitude], { icon })
          .bindPopup(popup, { maxWidth: 220 })
          .addTo(map)
          .on('click', () => onSelect(rider.rider_id));
        markersRef.current[rider.rider_id] = m;
      }
    });

    // Fit all markers in view on first load
    if (riders.length > 0 && !selectedId) {
      try {
        const group = L.featureGroup(Object.values(markersRef.current));
        map.fitBounds(group.getBounds().pad(0.3));
      } catch { }
    }

    // Open popup for selected rider
    if (selectedId && markersRef.current[selectedId]) {
      markersRef.current[selectedId].openPopup();
    }
  }, [riders, selectedId, onSelect]);

  return <div ref={mapRef} style={{ width: '100%', height: '100%', borderRadius: 12, zIndex: 0 }} />;
}

export default function LiveMapPage() {
  const [riders, setRiders] = useState([]);
  const [status, setStatus] = useState('retrying');
  const [selectedId, setSelectedId] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [countdown, setCountdown] = useState(4);
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    try {
      const data = await fetchLocations();
      setRiders(Array.isArray(data) ? data : []);
      setLastRefresh(new Date());
      setCountdown(4); // ✅ poll every 4s — matches the ~3s rider update rate
      setStatus('ok');
      firstLoad.current = false;
    } catch (err) {
      console.warn('[LiveMap]', err.message);
      setStatus('retrying');
      firstLoad.current = false;
    }
  }, []);

  // ✅ Poll every 4 seconds instead of 10 — keeps map nearly real-time
  usePolling(load, 4000);

  useEffect(() => {
    const t = setInterval(() => setCountdown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [lastRefresh]);

  const statusColor = status === 'ok' ? '#22c55e' : '#f59e0b';
  const statusText = status === 'ok'
    ? `${riders.length} rider${riders.length !== 1 ? 's' : ''} online · refreshes in ${countdown}s`
    : 'Connecting to server... (may take up to 30s on first load)';

  return (
    <div className="animate-fade-in" style={{ height: 'calc(100vh - 90px)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div className="page-header" style={{ marginBottom: 0 }}>
          <h1 className="page-title">Live Rider Map</h1>
          <p className="page-sub" style={{ color: statusColor }}>{statusText}</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {lastRefresh && (
            <span style={{ fontSize: 12, color: 'var(--gray-400)', fontFamily: 'var(--font-mono)' }}>
              Last: {formatTime(lastRefresh)}
            </span>
          )}
          <button className="btn btn-secondary btn-sm" onClick={load}>Refresh</button>
          {selectedId && <button className="btn btn-ghost btn-sm" onClick={() => setSelectedId(null)}>Clear</button>}
        </div>
      </div>

      {status === 'retrying' && (
        <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 10, padding: '10px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#f59e0b' }}>
          <span>Server waking up — this takes up to 30 seconds on first load. Map will appear automatically.</span>
          <button onClick={load} style={{ marginLeft: 'auto', background: 'none', border: '1px solid #f59e0b', color: '#f59e0b', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Retry Now</button>
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', gap: 16, minHeight: 0 }}>
        {/* ── Rider list sidebar ──────────────────────────────────────────── */}
        <div style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto' }}>
          {riders.length === 0 ? (
            <div className="card" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EmptyState
                icon={status === 'retrying' ? '' : ''}
                title={status === 'retrying' ? 'Connecting...' : 'No riders online'}
                subtitle={status === 'retrying' ? 'Server waking up' : 'Riders appear here during deliveries'}
              />
            </div>
          ) : riders.map((r, idx) => {
            const stale = staleness(r.updated_at);
            const isSelected = r.rider_id === selectedId;
            return (
              <div key={r.rider_id} onClick={() => setSelectedId(isSelected ? null : r.rider_id)}
                style={{
                  background: isSelected ? 'var(--primary)' : 'var(--white)',
                  border: `2px solid ${isSelected ? riderColor(idx) : 'var(--gray-200)'}`,
                  borderRadius: 12, padding: '12px 14px', cursor: 'pointer', transition: 'all 0.15s',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: riderColor(idx), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>🚴</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: isSelected ? 'var(--white)' : 'var(--gray-900)' }}>{r.rider_name}</div>
                    <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{r.rider_phone}</div>
                  </div>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: stale.fresh ? '#22c55e' : '#f59e0b' }} />
                </div>
                {r.order_id ? (
                  <div style={{ background: isSelected ? '#ffffff15' : 'var(--gray-50)', borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 3 }}>Active Order</div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: isSelected ? 'var(--accent)' : 'var(--primary)', fontFamily: 'var(--font-mono)' }}>#{r.order_id} · {r.order_package_id}</div>
                    <div style={{ fontSize: 11, color: isSelected ? 'var(--gray-300)' : 'var(--gray-500)', marginTop: 2 }}>→ {r.receiver_name}</div>
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>No active order</div>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <span style={{ fontSize: 10, color: stale.fresh ? '#22c55e' : '#f59e0b', fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
                    {stale.label}
                  </span>
                  {r.speed && (
                    <span style={{ fontSize: 10, color: 'var(--gray-400)', fontFamily: 'var(--font-mono)' }}>
                      {parseFloat(r.speed).toFixed(1)} km/h
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Map ─────────────────────────────────────────────────────────── */}
        <div style={{ flex: 1, borderRadius: 14, overflow: 'hidden', border: '1.5px solid var(--gray-200)', position: 'relative', minHeight: 400 }}>
          {riders.length === 0 && status === 'ok' && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(248,250,252,0.85)', zIndex: 10 }}>
              <EmptyState icon="" title="No active riders" subtitle="Map will update automatically when riders start deliveries." />
            </div>
          )}
          <LeafletMap riders={riders} selectedId={selectedId} onSelect={setSelectedId} />
          <div style={{ position: 'absolute', bottom: 16, right: 16, background: 'rgba(255,255,255,0.95)', borderRadius: 10, padding: '10px 14px', fontSize: 11, zIndex: 500, boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
            <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--gray-700)' }}>Legend</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e' }} /> Fresh (&lt;5 min)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b' }} /> Stale (&gt;5 min)
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}